import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import c from 'classnames';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import { DropdownOption } from '@wix/da-ds/pkg/Dropdown/types';
import ClockIcon from '@wix/da-ds/pkg/Icons/24x24/Clock';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import s from './TimeSelect.scss';

interface Props {
  selectedDate?: moment.Moment;
  className?: string;
  value?: string;
  disabled?: boolean;
  onSelect: (value: string) => void;
}

const TimeSelect: React.FC<Props> = ({
  className,
  selectedDate,
  value = '',
  disabled,
  onSelect,
}) => {
  const { t } = useTranslation();
  const isToday = useMemo(
    () =>
      selectedDate ? moment(selectedDate).isSame(new Date(), 'day') : false,
    [selectedDate]
  );
  const timeOptions: DropdownOption[] = useMemo(() => {
    const hour = moment().hour();
    return [
      { label: '12 AM', value: '0', disabled: isToday },
      { label: '1 AM', value: '1', disabled: isToday && hour >= 1 },
      { label: '2 AM', value: '2', disabled: isToday && hour >= 2 },
      { label: '3 AM', value: '3', disabled: isToday && hour >= 3 },
      { label: '4 AM', value: '4', disabled: isToday && hour >= 4 },
      { label: '5 AM', value: '5', disabled: isToday && hour >= 5 },
      { label: '6 AM', value: '6', disabled: isToday && hour >= 6 },
      { label: '7 AM', value: '7', disabled: isToday && hour >= 7 },
      { label: '8 AM', value: '8', disabled: isToday && hour >= 8 },
      { label: '9 AM', value: '9', disabled: isToday && hour >= 9 },
      { label: '10 AM', value: '10', disabled: isToday && hour >= 10 },
      { label: '11 AM', value: '11', disabled: isToday && hour >= 11 },
      { label: '12 PM', value: '12', disabled: isToday && hour >= 12 },
      { label: '1 PM', value: '13', disabled: isToday && hour >= 13 },
      { label: '2 PM', value: '14', disabled: isToday && hour >= 14 },
      { label: '3 PM', value: '15', disabled: isToday && hour >= 15 },
      { label: '4 PM', value: '16', disabled: isToday && hour >= 16 },
      { label: '5 PM', value: '17', disabled: isToday && hour >= 17 },
      { label: '6 PM', value: '18', disabled: isToday && hour >= 18 },
      { label: '7 PM', value: '19', disabled: isToday && hour >= 19 },
      { label: '8 PM', value: '20', disabled: isToday && hour >= 20 },
      { label: '9 PM', value: '21', disabled: isToday && hour >= 21 },
      { label: '10 PM', value: '22', disabled: isToday && hour >= 22 },
      { label: '11 PM', value: '23', disabled: isToday && hour >= 23 },
    ];
  }, [isToday]);

  return (
    <SelectField
      id="schedule-draft-time-picker"
      className={c(s['firefox-disable-option-color-hack'], className)}
      label={t('studio.modals.schedule.time.placeholder')}
      placeholder={t('studio.modals.schedule.time.placeholder')}
      prefix={<ClockIcon size={IconSize.SMALL} />}
      items={timeOptions}
      value={value}
      onSelect={onSelect}
      isLabelHidden
      disabled={disabled}
    />
  );
};
TimeSelect.displayName = 'TimeSelect';

export default TimeSelect;
